const ROW_TOP_TOLERANCE = 2;

/**
 * Reset current inline equal-height values so fresh measurements can be taken.
 * @param {HTMLElement[]} blocks
 */
function resetHeights(blocks) {
  for (const block of blocks) {
    block.style.removeProperty('--text-equal-height');
  }
}

/**
 * Find the element that receives min-height in CSS (`.text-block--equal-height > *`).
 * @param {HTMLElement} block
 * @returns {HTMLElement | null}
 */
function getMeasuredChild(block) {
  const child = block.firstElementChild;
  return child instanceof HTMLElement ? child : null;
}

/**
 * Set the review carousel track height from its selected slide's content.
 * @param {HTMLElement} carousel
 * @param {HTMLElement} [selectedCard]
 */
function setActiveSlideHeight(carousel, selectedCard) {
  const slides = carousel.querySelector(':scope > slideshow-container > slideshow-slides');
  if (!(slides instanceof HTMLElement)) return;

  const activeCard = selectedCard || Array.from(slides.children).find(
    (slide) => slide instanceof HTMLElement && slide.getAttribute('aria-hidden') === 'false'
  );
  if (!(activeCard instanceof HTMLElement)) return;

  const previousHeight = slides.style.height;
  slides.style.height = 'auto';
  const contentHeight = `${Math.ceil(activeCard.getBoundingClientRect().height)}px`;

  if (!previousHeight) {
    slides.style.height = contentHeight;
    return;
  }

  slides.style.height = previousHeight;
  if (previousHeight !== contentHeight) {
    requestAnimationFrame(() => {
      slides.style.height = contentHeight;
    });
  }
}

/**
 * Group blocks by visual row using top position.
 * @param {HTMLElement[]} blocks
 * @returns {HTMLElement[][]}
 */
function groupByRow(blocks) {
  const positioned = blocks
    .map((block) => ({
      block,
      top: Math.round(block.getBoundingClientRect().top),
      left: Math.round(block.getBoundingClientRect().left),
    }))
    .filter(({ block }) => block.offsetParent !== null)
    .sort((a, b) => (a.top - b.top) || (a.left - b.left));

  /** @type {HTMLElement[][]} */
  const rows = [];

  for (const item of positioned) {
    const row = rows[rows.length - 1];
    if (!row) {
      rows.push([item.block]);
      continue;
    }

    const rowTop = Math.round(row[0].getBoundingClientRect().top);
    if (Math.abs(item.top - rowTop) <= ROW_TOP_TOLERANCE) {
      row.push(item.block);
    } else {
      rows.push([item.block]);
    }
  }

  return rows;
}

/**
 * Apply equal min-height per visual row.
 */
function applyEqualHeights() {
  const rowContainers = Array.from(document.querySelectorAll('.layout-panel-flex--row')).filter(
    (el) => el instanceof HTMLElement
  );

  for (const container of rowContainers) {
    const blocks = Array.from(container.querySelectorAll('.text-block--equal-height')).filter(
      (el) => el instanceof HTMLElement
    );
    if (!blocks.length) continue;

    resetHeights(blocks);

    const rows = groupByRow(blocks);

    for (const row of rows) {
      let maxHeight = 0;

      for (const block of row) {
        const child = getMeasuredChild(block);
        if (!child) continue;
        maxHeight = Math.max(maxHeight, child.getBoundingClientRect().height);
      }

      if (!maxHeight) continue;

      const normalized = `${Math.ceil(maxHeight)}px`;
      for (const block of row) {
        block.style.setProperty('--text-equal-height', normalized);
      }
    }
  }
}

let rafId = 0;
function scheduleEqualHeights() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(() => {
    rafId = 0;
    applyEqualHeights();
  });
}

let carouselHeightRafId = 0;
function scheduleReviewCarouselHeights() {
  if (carouselHeightRafId) cancelAnimationFrame(carouselHeightRafId);
  carouselHeightRafId = requestAnimationFrame(() => {
    carouselHeightRafId = 0;
    const reviewCarousels = Array.from(document.querySelectorAll('.jffd-review-dynamic-height slideshow-component')).filter(
      (el) => el instanceof HTMLElement
    );
    for (const carousel of reviewCarousels) {
      setActiveSlideHeight(carousel);
    }
  });
}

const mutationObserver = new MutationObserver((mutations) => {
  scheduleEqualHeights();
  if (mutations.some((mutation) => mutation.type === 'childList' || mutation.attributeName === 'class')) {
    scheduleReviewCarouselHeights();
  }
});

window.addEventListener('resize', scheduleEqualHeights);
window.addEventListener('orientationchange', scheduleEqualHeights);
document.addEventListener('DOMContentLoaded', scheduleEqualHeights);
window.addEventListener('load', scheduleEqualHeights);

window.addEventListener('resize', scheduleReviewCarouselHeights);
window.addEventListener('orientationchange', scheduleReviewCarouselHeights);
document.addEventListener('DOMContentLoaded', scheduleReviewCarouselHeights);
window.addEventListener('load', scheduleReviewCarouselHeights);

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => {
    scheduleEqualHeights();
    scheduleReviewCarouselHeights();
  });
}

mutationObserver.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['class', 'style'],
});

document.addEventListener('slideshow:select', (event) => {
  const carousel = event.target;
  if (!(carousel instanceof HTMLElement) || !carousel.closest('.jffd-review-dynamic-height')) return;
  const selectedCard = event.detail?.slide;
  requestAnimationFrame(() => setActiveSlideHeight(carousel, selectedCard));
});

scheduleEqualHeights();
scheduleReviewCarouselHeights();
